import { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import Sidebar from '../../components/feature/Sidebar';
import OfflineIndicator from '../../components/base/OfflineIndicator';
import VueGlobaleTab from './components/VueGlobaleTab';
import ProductionTab from './components/ProductionTab';
import CommandesTab from './components/CommandesTab';
import VentesTab from './components/VentesTab';
import EquipeTab from './components/EquipeTab';
import { Toast, useToast } from '../../components/base/Toast';

interface UserInfo { id: string; nom: string; role: string; }

type TabKey = 'globale' | 'production' | 'commandes' | 'ventes' | 'equipe';

interface TabConfig {
  key: TabKey;
  label: string;
  icon: string;
  roles: string[];
  defaultFor: string[];
}

const TAB_CONFIG: TabConfig[] = [
  { key: 'globale',    label: 'Vue globale',  icon: 'ri-dashboard-2-line',    roles: ['gerant', 'superadmin'],                         defaultFor: ['gerant', 'superadmin'] },
  { key: 'commandes',  label: 'Commandes',    icon: 'ri-file-list-3-line',     roles: ['vendeur', 'gerant', 'superadmin', 'responsable_atelier'], defaultFor: ['vendeur'] },
  { key: 'production', label: 'Production',   icon: 'ri-layout-masonry-line',  roles: ['responsable_atelier', 'coupeur', 'tailleur', 'gerant', 'superadmin'], defaultFor: ['responsable_atelier', 'coupeur', 'tailleur'] },
  { key: 'ventes',     label: 'Ventes',       icon: 'ri-shopping-bag-line',    roles: ['vendeur', 'gerant', 'superadmin'],               defaultFor: [] },
  { key: 'equipe',     label: 'Équipe & Paie', icon: 'ri-team-line',          roles: ['gerant', 'superadmin'],                         defaultFor: [] },
];

function getDefaultTab(role: string): TabKey {
  for (const t of TAB_CONFIG) {
    if (t.defaultFor.includes(role)) return t.key;
  }
  return 'commandes';
}

export default function UnifiedDashboard() {
  const navigate = useNavigate();
  const [user, setUser] = useState<UserInfo | null>(null);
  const [activeTab, setActiveTab] = useState<TabKey>('commandes');
  const [sidebarCollapsed, setSidebarCollapsed] = useState(false);
  const { toast, showToast, hideToast } = useToast();

  useEffect(() => {
    const authError = sessionStorage.getItem('auth_error');
    if (authError) {
      showToast(authError, 'error');
      sessionStorage.removeItem('auth_error');
    }
  }, []);

  useEffect(() => {
    const stored = localStorage.getItem('atelier_bassoum_user');
    if (stored) {
      const u = JSON.parse(stored) as UserInfo;
      setUser(u);
      setActiveTab(getDefaultTab(u.role));
    } else {
      navigate('/login');
    }
  }, [navigate]);

  if (!user) return null;

  const visibleTabs = TAB_CONFIG.filter(t => t.roles.includes(user.role));
  const today = new Date().toLocaleDateString('fr-FR', { weekday: 'long', day: 'numeric', month: 'long' });

  const roleDisplayNames: Record<string, string> = {
    superadmin: 'Super Admin',
    gerant: 'Gérant',
    responsable_atelier: 'Resp. Atelier',
    vendeur: 'Vendeur',
    coupeur: 'Coupeur',
    tailleur: 'Tailleur',
  };

  const tabGreeting: Record<TabKey, string> = {
    globale: 'Vue d\'ensemble',
    commandes: 'Gestion des commandes',
    production: 'Suivi de production',
    ventes: 'Ventes & encaissements',
    equipe: 'Équipe & paie du personnel',
  };

  return (
    <div className="flex h-screen bg-gray-50 font-nunito overflow-hidden">
      <OfflineIndicator />
      {toast && <Toast message={toast.message} type={toast.type} onClose={hideToast} />}
      <div className="hidden md:block flex-shrink-0">
        <Sidebar
          role={user.role}
          nom={user.nom}
          collapsed={sidebarCollapsed}
          onToggle={() => setSidebarCollapsed(v => !v)}
        />
      </div>

      <div className="flex-1 flex flex-col overflow-hidden min-w-0">
        {/* Header */}
        <header className="bg-white border-b border-gray-100 px-4 md:px-6 py-3.5 flex items-center justify-between flex-shrink-0">
          <div className="flex items-center gap-3">
            {/* Hamburger mobile */}
            <button
              className="md:hidden w-9 h-9 flex items-center justify-center rounded-xl hover:bg-gray-100 cursor-pointer transition-colors text-gray-600"
              onClick={() => (window as any).toggleMobileMenu?.()}
              aria-label="Ouvrir le menu"
            >
              <i className="ri-menu-line text-xl" />
            </button>
            <div>
              <h1 className="font-playfair font-bold text-lg text-gray-900">{tabGreeting[activeTab]}</h1>
              <p className="text-xs text-gray-400 capitalize">{today} · {roleDisplayNames[user.role] || user.role}</p>
            </div>
          </div>
          <div className="flex items-center gap-3">
            <button
              onClick={() => navigate('/mesures')}
              className="hidden sm:flex items-center gap-2 text-xs bg-terre-50 text-terre-600 border border-terre-200 px-3 py-1.5 rounded-full font-semibold cursor-pointer hover:bg-terre-100 whitespace-nowrap"
            >
              <div className="w-3 h-3 flex items-center justify-center">
                <i className="ri-pencil-ruler-2-line" />
              </div>
              Mesures clients
            </button>
            <div className="w-8 h-8 rounded-full bg-foret-500 flex items-center justify-center text-white text-xs font-bold cursor-pointer flex-shrink-0">
              {user.nom.split(' ').map((n: string) => n[0]).join('').slice(0, 2).toUpperCase()}
            </div>
          </div>
        </header>

        {/* Smart Tab Filter */}
        <div className="bg-white border-b border-gray-100 px-6 flex-shrink-0">
          <div className="flex items-center gap-1">
            {visibleTabs.map(tab => (
              <button
                key={tab.key}
                onClick={() => setActiveTab(tab.key)}
                className={`flex items-center gap-2 px-4 py-3 text-sm font-semibold border-b-2 transition-all cursor-pointer whitespace-nowrap ${
                  activeTab === tab.key
                    ? 'border-foret-500 text-foret-600'
                    : 'border-transparent text-gray-400 hover:text-gray-700 hover:border-gray-200'
                }`}
              >
                <div className="w-4 h-4 flex items-center justify-center">
                  <i className={tab.icon} />
                </div>
                <span className="hidden sm:inline">{tab.label}</span>
              </button>
            ))}
          </div>
        </div>

        {/* Content */}
        <main className="flex-1 overflow-y-auto p-6">
          {activeTab === 'globale'    && <VueGlobaleTab />}
          {activeTab === 'production' && <ProductionTab userRole={user.role} userNom={user.nom} />}
          {activeTab === 'commandes'  && <CommandesTab />}
          {activeTab === 'ventes'     && <VentesTab />}
          {activeTab === 'equipe'     && <EquipeTab />}
        </main>
      </div>
    </div>
  );
}
